<template>
  <div class="md:w-[400px] w-full">
    <div class="text-lg font-medium">
      Настройки
    </div>

    <div class="flex flex-col gap-2.5 mt-6">
      <div class="bg-[#F9F9F9] border border-[#EEEEEE] rounded-xl p-4 cursor-pointer">
        <div
          class="flex items-center justify-between"
          @click="isAdmins = !isAdmins"
        >
          <div class="flex items-center">
            <img class="w-9 h-9 min-w-[32px]" :src="goalIcon" />
            <div class="font-medium ml-2">Администраторы</div>
          </div>

          <img
            class="w-6 h-6 min-w-[36px] transition-all duration-300"
            :src="chevronBottomIcon"
            :class="{ 'rotate-180': isAdmins }"
          />
        </div>

        <div v-if="isAdmins" class="mt-4">
          <div v-if="users.length" class="space-y-2 mb-4">
            <div
                v-for="(user, index) of users"
                :key="index"
                class="p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
            >
              <UserCard
                :avatarUrl="user.PERSONAL_PHOTO"
                :title="(user.LAST_NAME + ' ' + user.NAME).trim()"
                :subtitle="user.WORK_POSITION || 'Сотрудник'"
                size="md"
                shape="rounded-full"
              >
                <template #right>
                  <button
                      @click.stop="onDeleteUser(user.ID)"
                      class="w-7 h-7 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors flex-shrink-0"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </template>
              </UserCard>
            </div>
          </div>

          <button
              class="w-full h-10 rounded-lg bg-[#574AEA]/10 text-[#574AEA] font-medium hover:bg-[#574AEA]/20 transition-colors flex items-center justify-center gap-2 text-sm"
              @click="openUserPicker"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Добавить Администратора
          </button>
        </div>
      </div>

      <div
        v-if="!hasPublicApiUrl"
        class="bg-[#F9F9F9] border border-[#EEEEEE] rounded-xl p-4"
      >
        <div class="flex items-center justify-between mb-2">
          <div class="font-medium">Синхронизация с Bitrix24</div>
          <button
            class="h-9 px-3 rounded-lg bg-[#574AEA] text-white text-sm font-medium hover:bg-[#473add] disabled:opacity-60 disabled:cursor-not-allowed"
            @click="onRetryInstall"
            :disabled="isRetryingInstall"
          >
            {{ isRetryingInstall ? 'Синхронизация...' : 'Повторить синхронизацию' }}
          </button>
        </div>
        <p class="text-xs text-gray-500">
          Обновляет данные портала и подписки приложения на события задач в Bitrix24.
        </p>
      </div>
    </div>

    <!-- FEEDBACK -->
    <div class="mt-5 flex gap-2.5">
      <div
          class="w-[60%] bg-[#EDEBFD] text-[#4731E7] p-4 rounded-xl ease-in duration-200 cursor-pointer hover:scale-97"
          @click="toggleSupportModal"
      >
        <div class="flex items-center mb-2">
          <div class="text-[45px] mr-2">🤗</div>
          <div class="text-lg font-medium leading-5">
            Чего-то <br /> нехватает?
          </div>
        </div>
        <div class="leading-5">
          Свяжитесь с нами и мы разработаем приложение под вас.
        </div>

        <div class="flex justify-end">
          <img :src="chevronRightMainIcon" />
        </div>
      </div>

      <div
          class="w-[40%] bg-[#FBEFC2] text-[#DA9A03] flex flex-col justify-between p-4 rounded-xl ease-in duration-200 cursor-pointer hover:scale-97"
          @click="onOpenReview"
      >
        <div class="leading-5">
          Оставьте отзыв, помогите нам стать лучше.
        </div>

        <div class="flex justify-between items-end">
          <div class="text-[50px]">🤭</div>
          <img :src="chevronRightFeedbackIcon" />
        </div>
      </div>
    </div>
    <!-- ./FEEDBACK -->
  </div>

  <!-- Модальное окно выбора пользователя (администратор) -->
  <div v-if="showUserPicker" class="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-gray-200">
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div class="text-lg font-semibold text-gray-900">Добавить администратора</div>
        <button @click="closeUserPicker" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="p-6">
        <!-- Поле поиска по пользователям -->
        <input
          v-model="userSearch"
          type="text"
          placeholder="Найдите пользователя по имени или фамилии"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#574AEA] focus:border-transparent"
        />

        <!-- Список доступных пользователей -->
        <div class="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
          <div
            v-for="u in filteredCandidates"
            :key="u.ID"
            class="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
            @click="selectUser(u)"
          >
            <UserCard
              :avatarUrl="u.PERSONAL_PHOTO"
              :title="(u.LAST_NAME + ' ' + u.NAME).trim()"
              :subtitle="u.WORK_POSITION || 'Сотрудник'"
              size="md"
              shape="rounded-full"
            >
              <template #right>
                <div class="text-[#574AEA] text-sm font-medium">Выбрать</div>
              </template>
            </UserCard>
          </div>
          <div v-if="!filteredCandidates.length" class="p-3 text-sm text-gray-500">Ничего не найдено</div>
        </div>

        <div class="mt-4 flex justify-end">
          <button @click="closeUserPicker" class="py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">Отмена</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Модальное окно техподдержки -->
  <div v-if="showSupportModal" class="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-2xl max-w-md w-full shadow-xl border border-gray-200">
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div class="text-lg font-semibold text-gray-900">Обращение в тех. поддержку</div>
        <button @click="closeSupportModal" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div class="p-6">
        <div class="mb-6 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
          Возник вопрос или есть предложение, напишите нам и мы с вами свяжемся
        </div>
        
        <form @submit.prevent="submitSupportRequest" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Фамилия и Имя</label>
            <input
              type="text"
              v-model="supportForm.name"
              readonly
              class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              v-model="supportForm.email"
              readonly
              class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Телефон</label>
            <input
              type="tel"
              v-model="supportForm.phone"
              readonly
              class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Опишите вашу проблему, предложение</label>
            <textarea
              v-model="supportForm.message"
              rows="4"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#574AEA] focus:border-transparent"
              placeholder="Опишите детали вашего обращения..."
            ></textarea>
          </div>
          
          <div class="flex gap-3 pt-4">
            <button
              type="button"
              @click="closeSupportModal"
              class="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              :disabled="isSendingSupport || !supportForm.message.trim()"
              class="flex-1 py-2 px-4 bg-[#574AEA] text-white rounded-lg hover:bg-[#473add] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ isSendingSupport ? 'Отправка...' : 'Отправить' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, computed} from 'vue'
import { usePortalStore } from '@/stores/portalStore'
import { useToast } from "vue-toastification";
import apiService from '@/services/api'
import axios from 'axios'
import UserCard from '@/components/ui/UserCard.vue'

import goalIcon from '@/assets/icons/goal.svg';
import chevronBottomIcon from '@/assets/icons/chevron-bottom.svg';
import chevronRightMainIcon from '@/assets/icons/chevron-right-main.svg';
import chevronRightFeedbackIcon from '@/assets/icons/chevron-right-feedback.svg';

const portalStore = usePortalStore()

const toast = useToast();

const isAdmins = ref(false)
const admins = ref([])
// Состояние модального окна выбора пользователя
const showUserPicker = ref(false)
const userSearch = ref('')

// Модальное окно техподдержки
const showSupportModal = ref(false)
const isSendingSupport = ref(false)
const supportForm = ref({
  name: '',
  email: '',
  phone: '',
  message: ''
})

const isRetryingInstall = ref(false)
const hasPublicApiUrl = ref(false)

// Кандидаты на администраторы (все пользователи минус те, кто уже админы)
const candidateUsers = computed(() => {
  const all = Array.isArray(portalStore.portalData.allUsers) ? portalStore.portalData.allUsers : []
  const ids = new Set((Array.isArray(admins.value) ? admins.value : []).map((v) => String(v)))
  return all.filter((u) => !ids.has(String(u?.ID || '')))
})

// Фильтрация по строке поиска
const filteredCandidates = computed(() => {
  const q = (userSearch.value || '').trim().toLowerCase()
  if (!q) return candidateUsers.value
  return candidateUsers.value.filter((u) => `${u.LAST_NAME || ''} ${u.NAME || ''}`.toLowerCase().includes(q))
})

const users = computed(() => {
  const adminIds = Array.isArray(admins.value) ? admins.value : []
  const allUsers = Array.isArray(portalStore.portalData.allUsers) ? portalStore.portalData.allUsers : []
  return adminIds
    .map((id) => {
      const sid = typeof id === 'number' ? String(id) : String(id || '')
      const user = allUsers.find((i) => String(i?.ID || '') === sid)
      return user || null
    })
    .filter((i) => i && i.ID)
})


const toggleSupportModal = () => {
  const user = portalStore.portalData.user
  
  // Заполняем форму данными пользователя
  supportForm.value = {
    name: `${user.LAST_NAME || ''} ${user.NAME || ''}`.trim(),
    email: user.EMAIL || '',
    phone: user.PERSONAL_MOBILE || user.WORK_PHONE || '',
    message: ''
  }
  
  showSupportModal.value = true
}

const closeSupportModal = () => {
  showSupportModal.value = false
  supportForm.value.message = ''
}

const submitSupportRequest = async () => {
  if (!supportForm.value.message.trim()) {
    toast.error('Пожалуйста, опишите вашу проблему или предложение')
    return
  }
  
  isSendingSupport.value = true
  
  try {
    const requestData = {
      title: 'Запрос из приложения Тестирование сотрудников',
      name: supportForm.value.name,
      email: supportForm.value.email,
      phone: supportForm.value.phone,
      portal: portalStore.portalData.portal.domain,
      message: supportForm.value.message
    }
    
    await axios.post('https://dev.aventra.ru/app/_common/methods/create-request-from-app.php', requestData)
    
    toast.success('Обращение успешно отправлено! Мы свяжемся с вами в ближайшее время.')
    closeSupportModal()
    
  } catch (error) {
    console.error('Ошибка при отправке обращения:', error)
    toast.error('Ошибка при отправке обращения. Попробуйте позже.')
  } finally {
    isSendingSupport.value = false
  }
}

const onRetryInstall = async () => {
  if (isRetryingInstall.value) return
  isRetryingInstall.value = true
  try {
    await apiService.retryBitrixInstall()
    toast.success('Синхронизация запущена')
    void loadBitrixSettings()
  } catch (e) {
    console.error('Ошибка при повторной синхронизации:', e)
    toast.error('Не удалось запустить синхронизацию')
  } finally {
    isRetryingInstall.value = false
  }
}

const loadBitrixSettings = async () => {
  try {
    const data = await apiService.getBitrixSettings()
    const settings = (data && data.settings) || {}
    const url = String(settings.publicApiUrl || '').trim()
    hasPublicApiUrl.value = url.length > 0
  } catch (e) {
    hasPublicApiUrl.value = false
  }
}

const onOpenReview = () => {
  const domain = portalStore.portalData.portal.domain
  window.open(`https://${domain}/market/detail/aventra.obuchenie_sotrudnikov/`, '_blank');
}

// Открыть/закрыть модальное окно выбора пользователя
const openUserPicker = () => { userSearch.value = ''; showUserPicker.value = true }
const closeUserPicker = () => { showUserPicker.value = false }

// Добавление выбранного пользователя в администраторы
const selectUser = async (u) => {
  if (!u || !u.ID) return
  const userId = String(u.ID)
  if ((admins.value || []).some((x) => String(x) === userId)) {
    toast.warning('Пользователь уже добавлен')
    return
  }
  const updatedAdmins = [...(Array.isArray(admins.value) ? admins.value : []), userId]
  try {
    const portalId = portalStore.portalData.info.id
    await apiService.updatePortal(portalId, { admins: updatedAdmins })
    admins.value = updatedAdmins
    portalStore.setPortalData({
      ...portalStore.portalData,
      info: { ...portalStore.portalData.info, admins: updatedAdmins }
    })
    toast.success('Пользователь успешно добавлен как администратор')
    closeUserPicker()
  } catch (error) {
    console.error('Ошибка при добавлении администратора:', error)
    toast.error('Не удалось добавить пользователя как администратора')
  }
}

const onDeleteUser = async (id) => {
  // Фильтруем локальный массив администраторов
  const updatedAdmins = admins.value.filter((i) => i !== id);

  try {
    // Вызываем API для обновления списка администраторов после удаления
    // Предполагаем, что apiService имеет метод updatePortalAdmins(portalId, adminIds)
    const portalId = portalStore.portalData.info.id; // Получаем ID портала
    await apiService.updatePortal(portalId, { admins: updatedAdmins });

    // Обновляем локальное состояние и store после успешного API вызова
    admins.value = updatedAdmins;
    portalStore.setPortalData({
      ...portalStore.portalData,
      info: { ...portalStore.portalData.info, admins: updatedAdmins }
    });
    toast.success('Администратор успешно удален');

  } catch (error) {
    console.error('Ошибка при удалении администратора:', error);
    toast.error('Не удалось удалить администратора');
  }
}

onMounted(() => {
  const info = portalStore.portalData.info || {}
  admins.value = Array.isArray(info.admins) ? info.admins : []
  void loadBitrixSettings()
})
</script>
